import { Controller } from "stimulus"
import { DirectUpload } from "@rails/activestorage"

/**
 * Stimulus controller for the v2 license application wizard.
 * Handles step navigation, keyboard shortcuts, form submissions,
 * business search, and multi-contact management.
 */
export default class extends Controller {
  static get targets() {
    return [
      "step", "navItem", "navCheck", "banner",
      "prevButton", "nextButton", "saveButton",
      "businessFormElement", "businessForm", "businessSearchSidebar", "businessSearchResults",
      "einField", "dbaField",
      "locationIsContact", "primaryContactForm", "contactsContainer", "additionalContacts",
      "tasksContainer", "taskList", "noTasks",
      "documentFiles", "documentList"
    ]
  }

  get totalStepsValue() {
    return parseInt(this.data.get("totalSteps")) || 6
  }

  connect() {
    this.currentStep = 1
    this.completedSteps = new Set()
    this.businessId = null
    this.businessLocationId = null
    this.licenseTypeId = null
    this.applicationId = null
    this._hasTaskTypes = false
    this._taskTypesLoaded = false
    this._taskTypesPromise = Promise.resolve()
    this._locationCheckPromise = null
    this._searchQuery = null
    this._step6Loaded = false
    this._step6Loading = false
    this.bindKeyboardShortcuts()
    this.updateNavigation()
  }

  disconnect() {
    this.unbindKeyboardShortcuts()
    clearTimeout(this._searchTimeout)
  }

  // --- Navigation ---

  nextStep(e) {
    if (e) e.preventDefault()
    if (this.currentStep >= this.totalStepsValue) return

    switch (this.currentStep) {
      case 1:
        this.saveAndAdvanceStep1()
        return
      case 2:
        if (this._locationCheckPromise) {
          this._locationCheckPromise.then(() => {
            this._locationCheckPromise = null
            if (!this.businessLocationId) {
              this._showFlash("alert", "Please set the business location before continuing.")
              return
            }
            this.completedSteps.add(2)
            this.showStep(3)
          })
          return
        }
        if (!this.businessLocationId) {
          this._showFlash("alert", "Please set the business location before continuing.")
          return
        }
        break
      case 3:
        this.saveAndAdvanceStep3()
        return
      case 4:
        this.saveAndAdvanceStep4()
        return
      case 5:
        this.syncTaskTypeIds()
        break
    }

    this.completedSteps.add(this.currentStep)
    this.showStep(this.currentStep + 1)
  }

  previousStep(e) {
    if (e) e.preventDefault()
    if (this.currentStep <= 1) return
    var target = this.currentStep - 1
    if (target === 5 && this._taskTypesLoaded && !this._hasTaskTypes) target = 4
    this.showStep(target)
  }

  goToStep(e) {
    const step = parseInt(e.currentTarget.dataset.step)
    if (step === this.currentStep) return
    if (step > this.currentStep && !this.completedSteps.has(step - 1)) return
    this.showStep(step)
  }

  goToStepKeyboard(e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      this.goToStep(e)
    }
  }

  showStep(stepNumber) {
    // Sync task type selections when leaving step 5 via any navigation path
    if (this.currentStep === 5 && stepNumber !== 5) {
      this.syncTaskTypeIds()
    }

    // When entering step 2, check if business already has a location
    if (stepNumber === 2 && this.businessId && !this.businessLocationId) {
      this._locationCheckPromise = this._checkExistingLocation()
    } else {
      this._locationCheckPromise = null
    }

    // When entering step 6 (Revenue & Fees), ensure step 6 content is loaded
    if (stepNumber === this.totalStepsValue && this.applicationId) {
      this._ensureStep6Loaded()
    }

    this.stepTargets.forEach((el, index) => {
      el.classList.toggle("hidden", index + 1 !== stepNumber)
    })

    this.currentStep = stepNumber
    this.updateNavigation()
    this.clearBanner()
    this._clearFlash()
  }

  updateNavigation() {
    this.navItemTargets.forEach((item, index) => {
      const step = index + 1
      const isCurrent = step === this.currentStep
      item.classList.toggle("selected", isCurrent)
      item.classList.toggle("cursor-pointer", this.completedSteps.has(step) || isCurrent)
      if (isCurrent) {
        item.setAttribute("aria-current", "step")
      } else {
        item.removeAttribute("aria-current")
      }
    })

    this.navCheckTargets.forEach((check, index) => {
      check.classList.toggle("hidden", !this.completedSteps.has(index + 1))
    })

    // Hide tasks nav item (step 5, index 4) when no task types available
    if (this.navItemTargets.length >= 5) {
      this.navItemTargets[4].classList.toggle("hidden", !this._hasTaskTypes)
    }

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle("hidden", this.currentStep <= 1)
    }

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("hidden", this.currentStep >= this.totalStepsValue)
    }

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.toggle("hidden", this.currentStep !== this.totalStepsValue)
    }
  }

  // --- Keyboard Shortcuts ---

  bindKeyboardShortcuts() {
    this._keyHandler = (e) => {
      if (e.ctrlKey && e.key === ".") {
        e.preventDefault()
        this.nextStep()
      } else if (e.ctrlKey && e.key === ",") {
        e.preventDefault()
        this.previousStep()
      } else if (e.ctrlKey && e.key === "Enter") {
        e.preventDefault()
        this.submitCurrentStep()
      } else if (e.key === "Escape") {
        const active = document.activeElement
        const isInFormControl = active && (active.tagName === "INPUT" || active.tagName === "SELECT" || active.tagName === "TEXTAREA")
        if (!isInFormControl) {
          e.preventDefault()
          this.cancelWizard()
        }
      }
    }
    document.addEventListener("keydown", this._keyHandler)
  }

  unbindKeyboardShortcuts() {
    if (this._keyHandler) {
      document.removeEventListener("keydown", this._keyHandler)
    }
  }

  submitCurrentStep() {
    if (this.hasSaveButtonTarget && !this.saveButtonTarget.classList.contains("hidden")) {
      this.saveButtonTarget.click()
      return
    }
    const nextBtn = this.nextButtonTarget
    if (nextBtn && !nextBtn.classList.contains("hidden")) {
      nextBtn.click()
    }
  }

  cancelWizard() {
    if (confirm("Are you sure you want to cancel? Any unsaved progress will be lost.")) {
      window.history.back()
    }
  }

  preventFormSubmit(e) {
    e.preventDefault()
  }

  // --- Banner ---

  showBanner(message, linkUrl, linkText) {
    if (!this.hasBannerTarget) return

    this.bannerTarget.textContent = ""

    const messageSpan = document.createElement("span")
    messageSpan.textContent = message
    this.bannerTarget.appendChild(messageSpan)

    if (linkUrl && linkText) {
      this.bannerTarget.appendChild(document.createTextNode(" "))

      const linkEl = document.createElement("a")
      linkEl.href = linkUrl
      linkEl.target = "_blank"
      linkEl.rel = "noopener noreferrer"
      linkEl.className = "underline font-medium text-green-700 hover:text-green-900"
      linkEl.textContent = linkText
      this.bannerTarget.appendChild(linkEl)

      this.bannerTarget.appendChild(document.createTextNode(" "))

      const trailingSpan = document.createElement("span")
      trailingSpan.className = "text-green-600"
      trailingSpan.textContent = "or continue to the next step."
      this.bannerTarget.appendChild(trailingSpan)
    }

    this.bannerTarget.classList.remove("hidden")
  }

  clearBanner() {
    if (!this.hasBannerTarget) return
    this.bannerTarget.classList.add("hidden")
    this.bannerTarget.textContent = ""
  }

  // --- Step 1: Business Creation ---

  saveAndAdvanceStep1() {
    if (this.businessId) {
      this.completedSteps.add(1)
      this.showStep(2)
      return
    }

    const nameField = document.getElementById("business_business_name")
    if (!nameField || nameField.value.trim().length === 0) {
      this._showFlash("alert", "Please enter a business name.")
      return
    }

    this.createBusiness()
  }

  createBusiness(e) {
    if (e) e.preventDefault()
    if (!this.hasBusinessFormElementTarget) return

    const form = this.businessFormElementTarget
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    const formData = new FormData(form)
    const residencyField = document.getElementById("bl_license_application_residency")
    const businessIsLocalField = document.getElementById("business_business_is_local")
    if (residencyField && businessIsLocalField) {
      residencyField.checked = businessIsLocalField.checked
    }

    fetch(form.action, {
      method: form.method,
      body: formData,
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(response => {
        if (!response.ok) {
          const contentType = response.headers.get("content-type") || ""
          if (contentType.includes("application/json")) {
            return response.json().then(data => { throw new Error(this._normalizeErrors(data.errors || data) || "Error creating business") })
          }
          throw new Error("Error creating business")
        }
        return response.json()
      })
      .then(data => {
        this.businessId = data.business.id

        const businessIdField = document.querySelector("#business_location_business_id")
        if (businessIdField) businessIdField.value = data.business.id

        this.createEinRecord(data.business.id)
        this.createDbaRecord(data.business.id)
        this.uploadBusinessDocuments(data.business.id)

        this.completedSteps.add(1)
        this.showStep(2)
        this.showBanner(
          `Business "${data.business.business_name || ""}" has been created.`,
          `/businesses/${data.business.id}`,
          "View Business"
        )
      })
      .catch(error => {
        console.error("Error creating business:", error)
        this._showFlash("alert", error.message)
      })
  }

  createEinRecord(businessId) {
    if (!this.hasEinFieldTarget) return
    const ein = this.einFieldTarget.value.trim()
    if (ein.length === 0) return

    const piiTypeId = this.einFieldTarget.dataset.piiTypeId
    if (!piiTypeId) {
      console.warn("EIN field missing data-pii-type-id attribute")
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    fetch("/pii_data", {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        pii_datum: { dataset: ein, pii_type_id: piiTypeId, piiable_type: "Business", piiable_id: businessId }
      })
    })
      .then(response => {
        if (!response.ok) console.warn("EIN creation returned status:", response.status)
      })
      .catch(error => console.error("Error creating EIN:", error))
  }

  createDbaRecord(businessId) {
    if (!this.hasDbaFieldTarget) return
    const dba = this.dbaFieldTarget.value.trim()
    if (dba.length === 0) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    fetch(`/businesses/${businessId}/business_aliases`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        business_alias: { alias: dba, license: "1", license_listing: "1", business_id: businessId }
      })
    })
      .then(response => {
        if (!response.ok) console.warn("DBA creation returned status:", response.status)
      })
      .catch(error => console.error("Error creating DBA:", error))
  }

  searchBusinesses(e) {
    const query = e.target.value
    if (query.length < 3) {
      this._searchQuery = null
      if (this.hasBusinessSearchSidebarTarget) this.businessSearchSidebarTarget.classList.add("hidden")
      return
    }

    this._searchQuery = query
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => {
      var requestedQuery = query
      fetch(`/licensing/business_searches.json?name=${encodeURIComponent(query)}`, { credentials: "same-origin" })
        .then(response => {
          if (!response.ok) throw new Error("Search request failed")
          return response.json()
        })
        .then(data => {
          if (this._searchQuery !== requestedQuery) return
          if (!this.hasBusinessSearchResultsTarget) return
          const results = (data.businesses || data || []).slice(0, 10)

          this.businessSearchResultsTarget.textContent = ""

          results.forEach(b => {
            const li = document.createElement("li")
            li.className = "p-2 hover:bg-gray-100 rounded"

            const link = document.createElement("a")
            link.href = `/businesses/${b.id}`
            link.target = "_blank"
            link.rel = "noopener noreferrer"
            link.className = "text-purple-600 hover:text-purple-800"
            link.textContent = b.business_name || b.name || ""

            const span = document.createElement("span")
            span.className = "text-xs text-gray-400 ml-2"
            span.textContent = b.formation_type || ""

            li.appendChild(link)
            li.appendChild(span)
            this.businessSearchResultsTarget.appendChild(li)
          })

          if (this.hasBusinessSearchSidebarTarget) this.businessSearchSidebarTarget.classList.remove("hidden")
        })
        .catch(() => {
          if (this.hasBusinessSearchSidebarTarget) this.businessSearchSidebarTarget.classList.add("hidden")
        })
    }, 300)
  }

  // --- Step 2: Business Location ---

  setBusinessLocation(event) {
    event.preventDefault()
    const form = event.target.tagName === "FORM" ? event.target : event.target.form
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    const formData = new FormData(form)

    fetch(form.action, {
      method: form.method,
      body: formData,
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(response => {
        const contentType = response.headers.get("content-type") || ""

        if (!response.ok) {
          if (contentType.includes("application/json")) {
            return response.json().then(data => { throw new Error(this._normalizeErrors(data.errors || data) || "Error setting location") })
          }
          throw new Error("Error setting location")
        }

        if (response.redirected || !contentType.includes("application/json")) {
          throw new Error("Unable to set location. Please try again.")
        }

        return response.json()
      })
      .then(data => {
        this.businessLocationId = data.licensable_id

        const licensableIdField = document.querySelector("#bl_license_application_licensable_id")
        const licensableTypeField = document.querySelector("#bl_license_application_licensable_type")
        if (licensableIdField) {
          licensableIdField.value = data.licensable_id
          licensableTypeField.value = data.licensable_type
        }

        const licensableSubmitButton = document.querySelector(".wizard-licensable-submit")
        if (licensableSubmitButton) {
          licensableSubmitButton.classList.add("hidden")
        }
        this.showBanner("Business location set.", `/business_locations/${data.licensable_id}`, "View Location")
      })
      .catch(error => {
        console.error("Error setting location:", error)
        this._showFlash("alert", error.message)
      })
  }

  // --- Step 3: Create Application & Advance ---

  saveAndAdvanceStep3() {
    if (!this.licenseTypeId) {
      this._showFlash("alert", "Please select a license type before continuing.")
      return
    }

    var isUpdate = !!this.applicationId

    var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    var formData = new FormData()

    // Core application fields
    formData.append("bl_license_application[license_type_id]", this.licenseTypeId)
    if (!isUpdate) {
      formData.append("bl_license_application[current_units]", "0.0")
    }

    if (this.businessLocationId) {
      formData.append("bl_license_application[licensable_id]", this.businessLocationId)
      formData.append("bl_license_application[licensable_type]", "BusinessLocation")
    }

    // Classification from step 3 form
    var classField = document.querySelector("#bl_license_application_classification_id")
    if (classField && classField.value) {
      formData.append("bl_license_application[classification_id]", classField.value)
    }

    // License period from step 6 form if available
    var expField = document.querySelector("[name='bl_license_application[for_license_expiring_on]']")
    if (expField && expField.value) {
      formData.append("bl_license_application[for_license_expiring_on]", expField.value)
    }

    // For updates, include _method=patch in FormData
    if (isUpdate) {
      formData.append("_method", "patch")
    }

    var url = isUpdate
      ? "/bl_license_applications/" + this.applicationId
      : "/bl_license_applications"

    var self = this
    return fetch(url, {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(function(response) {
        if (!response.ok) {
          var contentType = response.headers.get("content-type") || ""
          if (contentType.indexOf("application/json") !== -1) {
            return response.json().then(function(data) {
              throw new Error(self._normalizeErrors(data.errors || data) || "Error saving application")
            })
          }
          throw new Error("Error saving application")
        }
        return response.json()
      })
      .then(function(data) {
        self.applicationId = data.id

        // Reload step 6 with full application context (form, fees, custom fields)
        self.reloadStep6(data.id)

        self.completedSteps.add(3)
        self.showStep(4)
        self.showBanner(
          isUpdate ? "Application updated." : "Application created.",
          "/bl_license_applications/" + data.id + "/edit",
          "View Application"
        )
      })
      .catch(function(error) {
        console.error("Error saving application:", error)
        self._showFlash("alert", error.message)
      })
  }

  // --- Step 3: License Type Selection ---

  handleLicenseTypeChange(e) {
    this.onLicenseTypeSelected(e.target.value)
  }

  onLicenseTypeSelected(licenseTypeId) {
    this.licenseTypeId = licenseTypeId
    this._clearTaskTypeIds()
    this._hasTaskTypes = false
    this._taskTypesLoaded = false
    this._taskTypesPromise = Promise.resolve()
    this.updateNavigation()
    this._step6Loaded = false

    if (!licenseTypeId) return
    this._taskTypesPromise = this.fetchTaskTypes(licenseTypeId)
  }

  fetchTaskTypes(licenseTypeId) {
    if (!this.hasTaskListTarget) return Promise.resolve()

    var requestedId = licenseTypeId

    return fetch(`/license_types/${licenseTypeId}/task_types`, { credentials: "same-origin" })
      .then(response => {
        if (this.licenseTypeId !== requestedId) return
        if (!response.ok) throw new Error("Error loading task types (status " + response.status + ")")
        return response.json()
      })
      .then(data => {
        if (!data || this.licenseTypeId !== requestedId) return

        const taskTypes = (data.task_types || [])
        this._hasTaskTypes = taskTypes.length > 0
        this._taskTypesLoaded = true
        this.updateNavigation()
        if (taskTypes.length === 0) {
          this.taskListTarget.textContent = ""
          if (this.hasNoTasksTarget) this.noTasksTarget.classList.remove("hidden")
          return
        }
        if (this.hasNoTasksTarget) this.noTasksTarget.classList.add("hidden")

        this.taskListTarget.textContent = ""

        const svgNS = "http://www.w3.org/2000/svg"

        taskTypes.forEach(tt => {
          const li = document.createElement("li")
          li.className = "add-menu-item p-2 border rounded cursor-pointer hover:bg-purple-50"

          const btn = document.createElement("button")
          btn.setAttribute("type", "button")
          btn.className = "text-gray-800 text-left inline-flex items-center w-full p-0 m-0 text-sm bg-transparent"
          btn.setAttribute("aria-pressed", "false")
          btn.dataset.action = "click->bl-wizard-v2#setTaskTypes"
          btn.dataset.id = String(tt.id)
          btn.dataset.description = tt.name

          const svg = document.createElementNS(svgNS, "svg")
          svg.setAttribute("class", "check-mark w-4 h-4 inline mr-2 text-gray-300")
          svg.setAttribute("aria-hidden", "true")
          svg.setAttribute("fill", "currentColor")
          svg.setAttribute("viewBox", "0 0 20 20")

          const path = document.createElementNS(svgNS, "path")
          path.setAttribute("fill-rule", "evenodd")
          path.setAttribute("d", "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z")
          path.setAttribute("clip-rule", "evenodd")

          svg.appendChild(path)
          btn.appendChild(svg)
          btn.appendChild(document.createTextNode("Add " + tt.name))

          li.appendChild(btn)
          this.taskListTarget.appendChild(li)
        })
      })
      .catch(error => {
        if (this.licenseTypeId !== requestedId) return
        console.error("Error fetching task types:", error)
        this._taskTypesLoaded = true
        this._hasTaskTypes = false
        this.updateNavigation()
        this._showFlash("alert", "Could not load task types. They will be skipped.")
      })
  }

  uploadBusinessDocuments(businessId) {
    if (!this.hasDocumentFilesTarget) return
    var files = this.documentFilesTarget.files
    if (!files || files.length === 0) return

    var uploadUrl = this.documentFilesTarget.getAttribute("data-direct-upload-url")
    var self = this

    var uploadPromises = Array.from(files).map(function(file) {
      return new Promise(function(resolve, reject) {
        var upload = new DirectUpload(file, uploadUrl)
        upload.create(function(error, blob) {
          if (error) {
            reject(error)
          } else {
            resolve(blob.signed_id)
          }
        })
      })
    })

    Promise.all(uploadPromises)
      .then(function(signedIds) {
        var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
        var formData = new FormData()
        signedIds.forEach(function(signedId) {
          formData.append("business[documents][]", signedId)
        })
        return fetch("/businesses/" + businessId, {
          method: "PATCH",
          body: formData,
          headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
          credentials: "same-origin"
        })
      })
      .catch(function(error) { console.error("Error uploading documents:", error) })
  }

  /**
   * Fetches the full step 6 (Revenue & Fees) content from the server and
   * replaces the step 6 div. This ensures dataset fields, calculator form,
   * and custom input fields all render correctly for the persisted application.
   */
  reloadStep6(applicationId) {
    var stepIndex = this.totalStepsValue - 1
    var stepEl = this.stepTargets[stepIndex]
    if (!stepEl) return Promise.resolve()
    if (this._step6Loading) return Promise.resolve()

    this._step6Loading = true

    var self = this
    return fetch("/bl_license_applications/" + applicationId + "/wizard_step_content", {
      credentials: "same-origin",
      headers: { "Accept": "text/html" }
    })
      .then(function(response) {
        if (!response.ok) throw new Error("Error loading step 6 (status " + response.status + ")")
        return response.text()
      })
      .then(function(html) {
        stepEl.innerHTML = html
        self._step6Loaded = true
        self._step6Loading = false
      })
      .catch(function(error) {
        console.error("Error reloading step 6:", error)
        self._step6Loaded = false
        self._step6Loading = false
      })
  }

  _ensureStep6Loaded() {
    if (this._step6Loaded || this._step6Loading || !this.applicationId) return
    this.reloadStep6(this.applicationId)
  }

  // --- Step 4: Contact Management ---

  saveAndAdvanceStep4() {
    const resolveNextStep = () => {
      return this._taskTypesPromise.then(() => {
        const skip = this._taskTypesLoaded && !this._hasTaskTypes
        return { nextStep: skip ? 6 : 5, skipTasks: skip }
      })
    }

    const advanceAfterContacts = () => {
      this._saveAllContactForms()
        .then(() => resolveNextStep())
        .then(({ nextStep, skipTasks }) => {
          this.completedSteps.add(4)
          if (skipTasks) this.completedSteps.add(5)
          this.showStep(nextStep)
        })
        .catch(error => {
          console.error("Error saving contacts:", error)
          this._showFlash("alert", "Error saving one or more contacts. Please try again.")
        })
    }

    if (!this.hasLocationIsContactTarget) {
      advanceAfterContacts()
      return
    }

    if (this.locationIsContactTarget.checked) {
      if (!this.businessId) {
        advanceAfterContacts()
        return
      }
      var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
      fetch("/businesses/" + this.businessId + "/contact_info_set_to_location", {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" }
      })
        .then(response => {
          if (!response.ok) {
            return response.json().then(data => {
              throw new Error(data.error || "Error setting contact to location")
            })
          }
          return this._saveAllContactForms()
        })
        .then(() => resolveNextStep())
        .then(({ nextStep, skipTasks }) => {
          this.completedSteps.add(4)
          if (skipTasks) this.completedSteps.add(5)
          this.showStep(nextStep)
        })
        .catch(error => {
          console.error("Error saving contacts:", error)
          this._showFlash("alert", error.message || "Error saving contact information. Please try again.")
        })
    } else {
      this._saveContactForm(this.primaryContactFormTarget, () => {
        advanceAfterContacts()
      })
    }
  }

  _saveContactForm(container, onSuccess) {
    if (!container) { if (onSuccess) onSuccess(); return }
    var form = container.querySelector("form.wizard-contact-form")
    if (!form) { if (onSuccess) onSuccess(); return }

    this._setContactableFields(form)
    var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    var formData = new FormData(form)
    var savedId = form.dataset.contactRelationId
    var url = savedId ? "/contact_relations/" + savedId : form.action
    var method = savedId ? "PATCH" : (form.method || "POST")

    fetch(url, {
      method: method,
      body: formData,
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(function(response) {
        if (!response.ok) throw new Error("Error saving contact")
        return response.json()
      })
      .then((data) => {
        if (data.contact_relation_id) {
          form.dataset.contactRelationId = data.contact_relation_id
        }
        if (onSuccess) onSuccess()
      })
      .catch(error => {
        console.error("Error saving contact:", error)
        this._showFlash("alert", error.message)
      })
  }

  _saveAllContactForms() {
    if (!this.hasAdditionalContactsTarget || !this.businessId) return Promise.resolve()

    var forms = this.additionalContactsTarget.querySelectorAll("form.wizard-contact-form")
    if (forms.length === 0) return Promise.resolve()

    var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    var self = this
    var promises = Array.from(forms).map(function(form) {
      self._setContactableFields(form)
      var formData = new FormData(form)
      var savedId = form.dataset.contactRelationId
      var url = savedId ? "/contact_relations/" + savedId : form.action
      var method = savedId ? "PATCH" : (form.method || "POST")
      return fetch(url, {
        method: method,
        body: formData,
        headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
        credentials: "same-origin"
      }).then(function(response) {
        if (!response.ok) throw new Error("Error saving additional contact")
        return response.json()
      }).then(function(data) {
        if (data.contact_relation_id) {
          form.dataset.contactRelationId = data.contact_relation_id
        }
      })
    })

    return Promise.all(promises)
  }

  _setContactableFields(form) {
    var contactableIdField = form.querySelector("[name*='[contactable_id]']")
    var contactableTypeField = form.querySelector("[name*='[contactable_type]']")
    if (contactableIdField) contactableIdField.value = this.businessId
    if (contactableTypeField) contactableTypeField.value = "Business"
  }

  toggleContactForm(e) {
    if (!this.hasPrimaryContactFormTarget) return
    if (this.locationIsContactTarget.checked) {
      this.primaryContactFormTarget.classList.add("hidden")
    } else {
      this.primaryContactFormTarget.classList.remove("hidden")
    }
  }

  addContact() {
    if (!this.hasAdditionalContactsTarget) return
    var self = this
    return fetch("/bl_license_applications/wizard_contact_form", { credentials: "same-origin" })
      .then(function(response) {
        if (!response.ok) throw new Error("Error loading contact form")
        var contentType = response.headers.get("content-type") || ""
        if (contentType.indexOf("text/html") === -1) throw new Error("Unexpected response type loading contact form")
        return response.text()
      })
      .then(function(html) {
        self.additionalContactsTarget.insertAdjacentHTML("beforeend", html)
      })
      .catch(function(error) {
        console.error("Error loading contact form:", error)
        self._showFlash("alert", "Error adding contact form. Please try again.")
      })
  }

  removeContact(e) {
    var wrapper = e.currentTarget.closest(".wizard-additional-contact")
    if (wrapper) wrapper.remove()
  }

  // --- Step 5: Tasks ---

  setTaskTypes(e) {
    e.preventDefault()
    const button = e.currentTarget
    const taskDescription = button.dataset.description
    const applicationTaskTypeField = document.getElementById("bl_license_application_task_type_ids_buffer")
    const li = button.closest("li")
    li.classList.toggle("selected")
    const isSelected = li.classList.contains("selected")
    button.setAttribute("aria-pressed", String(isSelected))
    const currentTasksFields = button.closest(".add-subset").querySelectorAll(".selected")
    applicationTaskTypeField.value = Array.from(currentTasksFields).map(el => el.children[0].dataset.id).join(",")
    this._showFlash("notice", "Task toggled: " + taskDescription)
  }

  syncTaskTypeIds() {
    const bufferField = document.getElementById("bl_license_application_task_type_ids_buffer")
    if (!bufferField) return

    const form = this.element.querySelector(".bl-license-application-form")
    if (!form) return

    let formField = form.querySelector("#bl_license_application_task_type_ids")
    if (!formField) {
      formField = document.createElement("input")
      formField.type = "hidden"
      formField.id = "bl_license_application_task_type_ids"
      formField.name = "bl_license_application[task_type_ids]"
      form.appendChild(formField)
    }

    formField.value = bufferField.value
  }

  // --- Step 6: Application Submission ---

  saveApplication(e) {
    if (e) e.preventDefault()

    var stepIndex = this.totalStepsValue - 1
    var stepEl = this.stepTargets[stepIndex]
    if (!stepEl) return

    var form = stepEl.querySelector("form")
    if (!form) return

    var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    var formData = new FormData(form)
    var self = this

    // Sync task type IDs into the form data
    var taskTypeField = document.getElementById("bl_license_application_task_type_ids")
    if (taskTypeField && taskTypeField.value) {
      formData.set("bl_license_application[task_type_ids]", taskTypeField.value)
    }

    fetch(form.action, {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(function(response) {
        if (!response.ok) return response.json().then(function(data) { throw new Error(self._normalizeErrors(data.errors || data.error) || "Error saving application") })
        return response.json()
      })
      .then(function(data) {
        window.location.href = "/bl_license_applications/" + data.id + "/edit"
      })
      .catch(function(error) {
        console.error("Error saving application:", error)
        self._showFlash("alert", error.message)
      })
  }

  submitApplication(e) {
    e.preventDefault()
    var form = e.target
    if (form.tagName !== "FORM") form = form.querySelector("form") || form.closest("form")
    if (!form) return

    var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    var formData = new FormData(form)
    var self = this

    fetch(form.action, {
      method: form.method || "POST",
      body: formData,
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(function(response) {
        if (!response.ok) return response.json().then(function(data) { throw new Error(self._normalizeErrors(data.errors || data.error) || "Error creating application") })
        return response.json()
      })
      .then(function(data) {
        window.location.href = "/bl_license_applications/" + data.id + "/edit"
      })
      .catch(function(error) {
        console.error("Error submitting application:", error)
        self._showFlash("alert", error.message)
      })
  }

  // --- Helpers ---

  _showFlash(type, message) {
    const flashArea = document.getElementById("flash-area")
    if (!flashArea) return
    flashArea.textContent = ""
    const p = document.createElement("p")
    p.className = type
    p.textContent = message
    flashArea.appendChild(p)
  }

  _clearFlash() {
    const flashArea = document.getElementById("flash-area")
    if (flashArea) flashArea.textContent = ""
  }

  _clearTaskTypeIds() {
    const bufferField = document.getElementById("bl_license_application_task_type_ids_buffer")
    if (bufferField) bufferField.value = ""
  }

  _checkExistingLocation() {
    return fetch(`/businesses/${this.businessId}/business_locations.json`, { credentials: "same-origin" })
      .then(response => {
        if (!response.ok) return
        return response.json()
      })
      .then(data => {
        if (!data) return
        const locations = Array.isArray(data) ? data : (data.business_locations || [])
        if (locations.length > 0) {
          const loc = locations[0]
          this.businessLocationId = loc.id

          const licensableIdField = document.querySelector("#bl_license_application_licensable_id")
          const licensableTypeField = document.querySelector("#bl_license_application_licensable_type")
          if (licensableIdField) {
            licensableIdField.value = loc.id
            licensableTypeField.value = "BusinessLocation"
          }

          const submitBtn = document.querySelector(".wizard-licensable-submit")
          if (submitBtn) submitBtn.classList.add("hidden")

          this.showBanner("Business location already set.", `/business_locations/${loc.id}`, "View Location")
        }
      })
      .catch(error => console.error("Error checking existing location:", error))
  }

  _normalizeErrors(errors) {
    if (!errors) return null
    if (typeof errors === "string") return errors
    if (Array.isArray(errors)) return errors.join(", ")
    if (typeof errors === "object") {
      const messages = []
      for (const key in errors) {
        const vals = Array.isArray(errors[key]) ? errors[key] : [errors[key]]
        vals.forEach(v => messages.push(key + " " + v))
      }
      return messages.join(", ")
    }
    return String(errors)
  }
}
